import React, { useRef, useEffect, type FocusEvent, useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { styledComponentWithCondition } from '@atlassian/jira-compiled-migration';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { MENU_PLACEHOLDER_ID } from '../../constants';
import messages from '../../messages';
import { MenuPlaceholderControl } from '../../styled';

type Props = {
	isFocused: boolean;
	cardKey: string;
	summary: string;
	onFocus: (event: FocusEvent<HTMLElement>) => void;
	onBlur: (event: FocusEvent<HTMLElement>) => void;
	onClick: (event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
	onKeyDown: (event: React.KeyboardEvent, analyticsEvent: UIAnalyticsEvent) => void;
};

const InteractionLayer = ({
	cardKey,
	summary,
	onClick,
	onKeyDown,
	onFocus = noop,
	onBlur = noop,
	isFocused = false,
}: Props) => {
	const ref = useRef<HTMLElement>(null);
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (isFocused === true && ref.current !== null && document.activeElement !== ref.current) {
			ref.current.focus();
		}
	}, [isFocused]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'issue',
			});
			onClick(e, analyticsEvent);
		},
		[onClick, createAnalyticsEvent],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'issue',
			});
			onKeyDown(e, analyticsEvent);
		},
		[onKeyDown, createAnalyticsEvent],
	);

	return (
		<Container
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			tabIndex={0}
			role="button"
			aria-label={`${cardKey} ${summary}. ${formatMessage(messages.cardFocusScreenreaderText)}`}
			data-testid="software-backlog.card-list.card.card-contents.interaction-layer.accessible-card"
			onFocus={onFocus}
			onBlur={onBlur}
			innerRef={ref}
		/>
	);
};

export default InteractionLayer;

const cardFocusStylesOld = `
    content: '';
    display: block;
    position: absolute;
    z-index: 1;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    box-shadow: 0 0 0 2px ${token('color.border.focused', colors.B200)};
    pointer-events: none;
`;

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerControl = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	&:focus {
		outline: none;
		& ~ ${MenuPlaceholderControl} {
			visibility: visible;
		}
		&::after {
			${cardFocusStylesOld}
		}
	}

	/* Edge */
	@supports (-ms-ime-align: auto) {
		&:focus::after {
			${cardFocusStylesOld}
			box-shadow: inset 0 0 0 2px ${token('color.border.focused', colors.B200)};
		}
	}

	/* IE11 */
	@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
		&:focus::after {
			${cardFocusStylesOld}
			box-shadow: inset 0 0 0 2px ${token('color.border.focused', colors.B200)};
		}
	}
`;

const cardFocusStyles = {
	content: '',
	display: 'block',
	position: 'absolute',
	zIndex: 1,
	left: 0,
	right: 0,
	top: 0,
	bottom: 0,
	boxShadow: `0 0 0 2px ${token('color.border.focused', colors.B200)}`,
	pointerEvents: 'none',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContainerExperiment = styled2.div({
	position: 'absolute',
	top: 0,
	bottom: 0,
	left: 0,
	right: 0,
	'&:focus': {
		outline: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		[`& ~ [data-component-selector="${MENU_PLACEHOLDER_ID}"]`]: {
			visibility: 'visible',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		'&::after': cardFocusStyles,
	},
	'@supports (-ms-ime-align: auto)': {
		'&:focus::after': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			...cardFocusStyles,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			boxShadow: `inset 0 0 0 2px ${token('color.border.focused', colors.B200)}`,
		},
	},
	'@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)': {
		'&:focus::after': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			...cardFocusStyles,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			boxShadow: `inset 0 0 0 2px ${token('color.border.focused', colors.B200)}`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Container = styledComponentWithCondition(
	() => ff('compiled.migration.jsw.tanuki'),
	ContainerExperiment,
	ContainerControl,
);
